"use client";

import Image from "next/image";
import { CalendarDays } from "lucide-react";
import type { Event } from "@/lib/api-types";
import { Badge } from "@/components/ui/Badge";
import { cn } from "@/lib/utils";

interface EventCardProps {
  event: Event;
  className?: string;
  interactive?: boolean;
  variant?: "default" | "slideshow";
}

function isDataUrl(src: string) {
  return src.startsWith("data:");
}

function eventStatusLabel(status: string) {
  return status.toLowerCase() === "completed" ? "completed" : "upcoming";
}

export function EventCard({
  event,
  className,
  interactive = false,
  variant = "default",
}: EventCardProps) {
  const imageSrc = event.images?.[0];
  const status = eventStatusLabel(event.status);
  const isSlideshow = variant === "slideshow";

  return (
    <article
      className={cn(
        "site-card overflow-hidden",
        isSlideshow ? "flex h-full flex-col" : "h-full",
        interactive &&
          "transition-all duration-300 hover:-translate-y-1 hover:shadow-lg",
        className
      )}
    >
      <div
        className={cn(
          "relative w-full overflow-hidden bg-gradient-to-br from-[var(--site-accent)]/15 to-[var(--site-bg)]",
          isSlideshow ? "min-h-[11rem] flex-[1.4]" : "aspect-video"
        )}
      >
        {imageSrc ? (
          isDataUrl(imageSrc) ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={imageSrc}
              alt=""
              className="h-full w-full object-cover transition-transform duration-500 ease-[var(--ease-out-expo)] group-hover:scale-105"
            />
          ) : (
            <Image
              src={imageSrc}
              alt=""
              fill
              className="object-cover transition-transform duration-500 ease-[var(--ease-out-expo)] group-hover:scale-105"
              sizes="(max-width:768px) 100vw, 33vw"
            />
          )
        ) : (
          <div className="flex h-full items-center justify-center text-[var(--site-accent)]/40">
            <CalendarDays size={40} aria-hidden />
          </div>
        )}
      </div>
      <div
        className={cn(
          isSlideshow
            ? "flex shrink-0 flex-col gap-2 overflow-hidden p-4"
            : "p-5"
        )}
      >
        <div
          className={cn(
            "flex items-center justify-between gap-2",
            isSlideshow && "overflow-hidden"
          )}
        >
          <div
            className={cn(
              "flex items-center gap-1.5",
              isSlideshow
                ? "min-w-0 flex-nowrap overflow-hidden"
                : "flex-wrap"
            )}
          >
            <Badge
              className={cn(
                "shrink-0",
                status === "completed"
                  ? "border-green-500/30 bg-green-500/20 text-green-400"
                  : "border-[var(--site-accent)]/30 bg-[var(--site-accent)]/20 text-[var(--site-accent)]"
              )}
            >
              {status}
            </Badge>
            {event.featured ? (
              <Badge className="shrink-0 border-[var(--charity-gold)]/40 bg-[var(--charity-gold)]/15 text-[var(--charity-green-dark)]">
                Featured
              </Badge>
            ) : null}
          </div>
          <span
            className={cn(
              "shrink-0 text-xs text-[var(--site-muted)]",
              isSlideshow && "max-w-[40%] truncate"
            )}
          >
            {event.date.trim() ? event.date : null}
          </span>
        </div>
        <h3
          className={cn(
            "font-bold text-[var(--site-text)]",
            isSlideshow ? "line-clamp-2 leading-snug" : "mb-2"
          )}
        >
          {event.title}
        </h3>
        <p
          className={cn(
            "line-clamp-2 text-sm text-[var(--site-muted)]",
            isSlideshow && "leading-snug"
          )}
        >
          {event.description}
        </p>
        {isSlideshow ? (
          <p className="line-clamp-1 text-xs font-medium leading-snug text-[var(--site-accent)]">
            {event.items || "\u00A0"}
          </p>
        ) : event.items ? (
          <p className="mt-2 text-xs font-medium text-[var(--site-accent)]">
            {event.items}
          </p>
        ) : null}
      </div>
    </article>
  );
}
